<script setup lang="ts">
import { onBeforeUnmount, ref } from 'vue'

import { seriesSites } from '../../data/siteContent'
import { primaryLogoUrl } from '../../lib/brand'

const portalActive = ref(false)
const targetUrl = ref('')
const targetName = ref('')
const targetTransition = ref('')
let navigationTimer = 0

function navigateNow() {
  if (targetUrl.value) window.location.assign(targetUrl.value)
}

function handleSiteClick(event: MouseEvent, href: string | null, english: string) {
  if (!href || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
  event.preventDefault()
  targetUrl.value = href
  targetName.value = english
  targetTransition.value =
    {
      'FRESHMAN OS': '专业宇宙正在切换为身份迁移系统',
      'FRESHMAN SIMULATOR': '专业世界已经开启，但你的第一段大学故事还没有发生',
      'ZERO LAB': 'SE-01 校园配送任务正在展开，先观察程序怎样运行',
      'DEVELOPER LAB': '开发者工作台已经准备好，从项目文件开始观察',
      'SE DESK': '办事入口、常用电话、公众号和学长经验。',
    }[english] ?? '数字宇宙正在展开为课程蓝图'
  portalActive.value = true
  navigationTimer = window.setTimeout(navigateNow, 480)
}

onBeforeUnmount(() => window.clearTimeout(navigationTimer))
</script>

<template>
  <section id="series" class="section series-section" aria-labelledby="series-title">
    <div class="series-section__beam" aria-hidden="true" />
    <div class="section-frame">
      <div class="section-heading section-heading--split" data-reveal>
        <div>
          <p class="section-kicker">05 / THE CONSTELLATION</p>
          <h2 id="series-title">系列计划入口</h2>
        </div>
        <p>SE Universe 是主航站。更多面向新生的数字服务，正在同一品牌宇宙中建立坐标。</p>
      </div>

      <div class="series-routes">
        <component
          :is="site.href ? 'a' : 'article'"
          v-for="(site, index) in seriesSites"
          :key="site.directory"
          class="series-route"
          :class="{
            'series-route--online': Boolean(site.href),
            'series-route--zero-lab': site.id === 'se-zero-lab',
            'series-route--developer-lab': site.id === 'developer-lab',
          }"
          :style="{ '--index': index }"
          :href="site.href ?? undefined"
          :data-site-id="site.id"
          :data-testid="
            site.id === 'curriculum-atlas'
              ? 'curriculum-atlas-entry'
              : site.id === 'freshman-os'
                ? 'freshman-os-entry'
                : site.id === 'freshman-simulator'
                  ? 'freshman-simulator-entry'
                  : site.id === 'se-zero-lab'
                    ? 'zero-lab-entry'
                    : site.id === 'developer-lab'
                      ? 'developer-lab-entry'
                      : site.id === 'se-desk'
                        ? 'se-desk-entry'
                        : undefined
          "
          :data-recommendation="site.id === 'developer-lab' ? 'developer-lab' : undefined"
          data-reveal
          @click="handleSiteClick($event, site.href, site.english)"
        >
          <p>{{ String(index + 1).padStart(2, '0') }} / {{ site.english }}</p>
          <h3>{{ site.name }}</h3>
          <span>{{ site.description }}</span>
          <strong v-if="site.id === 'freshman-simulator'" class="series-route__action">
            体验大学第一个月 →
          </strong>
          <strong v-else-if="site.id === 'se-zero-lab'" class="series-route__action">
            进入 SE-01 校园配送任务 →
          </strong>
          <p v-if="site.id === 'se-zero-lab'" class="series-route__device-note">
            <b>推荐使用电脑体验</b>
            本实验包含任务拖动、程序运行观察和 C++ 代码联动。电脑端能够获得完整的编排和调试体验。
          </p>
          <div>
            <code>apps/{{ site.directory }}</code>
            <small>{{ site.status }}</small>
          </div>
        </component>
      </div>
    </div>

    <Transition name="series-portal">
      <div
        v-if="portalActive"
        class="series-transition"
        role="dialog"
        :aria-label="`正在进入 ${targetName}`"
      >
        <div class="series-transition__mark" aria-hidden="true">
          <img :src="primaryLogoUrl" alt="" width="88" height="88" />
          <i /><i /><i /><i />
        </div>
        <p>SE UNIVERSE → {{ targetName }}</p>
        <strong>{{ targetTransition }}</strong>
        <button type="button" @click="navigateNow">立即进入</button>
      </div>
    </Transition>
  </section>
</template>
